import React, { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { toast } from "react-toastify";
import { FiSave, FiPlus, FiTrash2, FiUpload, FiUser } from "react-icons/fi";
import { profileAPI } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";

const Profile = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      name: "",
      title: "",
      detailbio: "",
      bio: "",
      social: {
        email: "",
        phone: "",
        linkedin: "",
        github: "",
        twitter: "",
      },
      experience: [],
      education: [],
    },
  });

  const {
    fields: experienceFields,
    append: appendExperience,
    remove: removeExperience,
  } = useFieldArray({
    control,
    name: "experience",
  });

  const {
    fields: educationFields,
    append: appendEducation,
    remove: removeEducation,
  } = useFieldArray({
    control,
    name: "education",
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await profileAPI.getProfile();
      const profile = response.data;

      setValue("name", profile.name || "");
      setValue("title", profile.title || "");
      setValue("bio", profile.bio || "");
      setValue("detailbio", profile.detailbio || "");
      setValue("social", {
        email: profile.social?.email || "",
        phone: profile.social?.phone || "",
        linkedin: profile.social?.linkedin || "",
        github: profile.social?.github || "",
        twitter: profile.social?.twitter || "",
      });

      if (profile.experience) {
        profile.experience.forEach((exp) => appendExperience(exp));
      }

      if (profile.education) {
        profile.education.forEach((edu) => appendEducation(edu));
      }

      if (profile.avatar) {
        setAvatarPreview(
          `${process.env.REACT_APP_API_URL || "http://localhost:5000"}${
            profile.avatar
          }`
        );
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error("Failed to fetch profile");
      }
    } finally {
      setInitialLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      const formData = new FormData();

      // Basic info
      formData.append("name", data.name);
      formData.append("title", data.title);
      formData.append("bio", data.bio);
      formData.append("detailbio", data.detailbio);

      // Social links
      formData.append("social", JSON.stringify(data.social));

      // Experience and education
      formData.append("experience", JSON.stringify(data.experience));
      formData.append("education", JSON.stringify(data.education));

      // Files
      const avatarInput = document.querySelector('input[name="avatar"]');
      if (avatarInput?.files[0]) {
        formData.append("avatar", avatarInput.files[0]);
      }

      const resumeInput = document.querySelector('input[name="resume"]');
      if (resumeInput?.files[0]) {
        formData.append("resume", resumeInput.files[0]);
      }

      await profileAPI.updateProfile(formData);
      toast.success("Profile updated successfully");

      // Refresh to show updated data
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Resume file size should be less than 10MB");
        return;
      }
      setResumeFile(file);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="text-gray-600">
          Manage your personal information and professional details
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">Basic Information</h2>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      className={`form-input ${
                        errors.name ? "border-red-500" : ""
                      }`}
                      placeholder="Your full name"
                      {...register("name", { required: "Name is required" })}
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Professional Title *</label>
                    <input
                      type="text"
                      className={`form-input ${
                        errors.title ? "border-red-500" : ""
                      }`}
                      placeholder="e.g., Full Stack Developer"
                      {...register("title", { required: "Title is required" })}
                    />
                    {errors.title && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.title.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="form-label">Bio *</label>
                  <textarea
                    rows={4}
                    className={`form-input ${
                      errors.bio ? "border-red-500" : ""
                    }`}
                    placeholder="Tell us about yourself, your skills, and experience"
                    {...register("bio", { required: "Bio is required" })}
                  />
                  {errors.bio && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.bio.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="form-label">Mini Bio *</label>
                  <textarea
                    rows={4}
                    className={`form-input ${
                      errors.detailbio ? "border-red-500" : ""
                    }`}
                    placeholder="Tell us about yourself, your skills, and experience"
                    {...register("detailbio", {
                      required: "Detail Bio is required",
                    })}
                  />
                  {errors.detailbio && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.detailbio.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">
                Contact & Social Links
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="your.email@example.com"
                    {...register("social.email")}
                  />
                </div>

                <div>
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="+1 (555) 123-4567"
                    {...register("social.phone")}
                  />
                </div>

                <div>
                  <label className="form-label">LinkedIn</label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://linkedin.com/in/username"
                    {...register("social.linkedin")}
                  />
                </div>

                <div>
                  <label className="form-label">GitHub</label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://github.com/username"
                    {...register("social.github")}
                  />
                </div>

                <div>
                  <label className="form-label">Twitter</label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://twitter.com/username"
                    {...register("social.twitter")}
                  />
                </div>
              </div>
            </div>

            {/* Experience */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Experience</h2>
                <button
                  type="button"
                  onClick={() =>
                    appendExperience({
                      company: "",
                      position: "",
                      duration: "",
                      description: "",
                    })
                  }
                  className="btn btn-secondary !py-1 !px-3 !text-sm"
                >
                  <FiPlus size={14} className="mr-1" />
                  Add
                </button>
              </div>

              <div className="space-y-4">
                {experienceFields.map((field, index) => (
                  <div
                    key={field.id}
                    className="p-4 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-gray-900">
                        Experience #{index + 1}
                      </h3>
                      <button
                        type="button"
                        onClick={() => removeExperience(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Company name"
                          {...register(`experience.${index}.company`)}
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Position/Role"
                          {...register(`experience.${index}.position`)}
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Duration (e.g., 2020-2023)"
                          {...register(`experience.${index}.duration`)}
                        />
                      </div>
                    </div>

                    <div className="mt-3">
                      <textarea
                        rows={3}
                        className="form-input"
                        placeholder="Job description and achievements"
                        {...register(`experience.${index}.description`)}
                      />
                    </div>
                  </div>
                ))}

                {experienceFields.length === 0 && (
                  <p className="text-gray-500 text-center py-4">
                    No experience added yet
                  </p>
                )}
              </div>
            </div>

            {/* Education */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Education</h2>
                <button
                  type="button"
                  onClick={() =>
                    appendEducation({
                      institution: "",
                      degree: "",
                      duration: "",
                      description: "",
                    })
                  }
                  className="btn btn-secondary !py-1 !px-3 !text-sm"
                >
                  <FiPlus size={14} className="mr-1" />
                  Add
                </button>
              </div>

              <div className="space-y-4">
                {educationFields.map((field, index) => (
                  <div
                    key={field.id}
                    className="p-4 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-gray-900">
                        Education #{index + 1}
                      </h3>
                      <button
                        type="button"
                        onClick={() => removeEducation(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Institution name"
                          {...register(`education.${index}.institution`)}
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Degree/Course"
                          {...register(`education.${index}.degree`)}
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Duration (e.g., 2016-2020)"
                          {...register(`education.${index}.duration`)}
                        />
                      </div>
                    </div>

                    <div className="mt-3">
                      <textarea
                        rows={2}
                        className="form-input"
                        placeholder="Additional details (optional)"
                        {...register(`education.${index}.description`)}
                      />
                    </div>
                  </div>
                ))}

                {educationFields.length === 0 && (
                  <p className="text-gray-500 text-center py-4">
                    No education added yet
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Avatar Upload */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">Profile Picture</h2>

              <div className="text-center">
                <div className="relative inline-block">
                  <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4 overflow-hidden">
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FiUser className="text-gray-400" size={32} />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <input
                    type="file"
                    name="avatar"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                    id="avatar-upload"
                  />
                  <label
                    htmlFor="avatar-upload"
                    className="btn btn-secondary cursor-pointer !text-sm"
                  >
                    <FiUpload className="mr-2" size={14} />
                    Upload Photo
                  </label>
                  <p className="text-gray-500 text-xs mt-2">Max 5MB, JPG/PNG</p>
                </div>
              </div>
            </div>

            {/* Resume Upload */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">Resume</h2>

              <div>
                <input
                  type="file"
                  name="resume"
                  accept=".pdf"
                  onChange={handleResumeChange}
                  className="hidden"
                  id="resume-upload"
                />
                <label
                  htmlFor="resume-upload"
                  className="btn btn-secondary cursor-pointer w-full !text-sm"
                >
                  <FiUpload className="mr-2" size={14} />
                  {resumeFile ? resumeFile.name : "Upload Resume"}
                </label>
                <p className="text-gray-500 text-xs mt-2">
                  PDF format, max 10MB
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="card p-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave className="mr-2" size={16} />
                    Save Profile
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Profile;
